// src/common/verification/workflow.js
import { parseFootnote } from './parse-stage.js';
import { resolveCitation } from './resolve-stage.js';
import { analyzeCitation, analyzeJointGroup } from './analyze-stage.js';
import { extractErrorMessage } from '../utils/error-utils.js';
import { pickRichestCitation, resolutionKeyFromCitation } from '../utils/citation-utils.js';
import { updateCitationUI } from '../ui.js';

/**
 * Execute the full verification workflow for a single footnote: parse → resolve → analyze
 * 
 * @param {Object} footnote - The footnote to verify
 * @param {Object} options - Configuration options
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {boolean} options.isWebApp - Whether running in web app mode
 * @param {Object} options.commonPayload - Common payload for API calls
 * @param {boolean} options.skipCache - Whether to skip cache (for "Check Again")
 * @param {boolean} options.recheckCongruence - Reuse successful resolutions but
 *   run congruence again.
 * @param {HTMLElement} options.resultsContainer - Container element for UI updates
 * @param {Function} options.onProgress - Progress callback (stage, message)
 * @param {Function} options.onParsed - Callback after parse returns (claim, citations)
 * @returns {Promise<Object>} Final results object keyed by citation ID
 */
export async function verifyFootnoteWorkflow(footnote, options = {}) {
  const {
    apiBase,
    getAuthHeaders,
    isWebApp = false,
    commonPayload = {},
    skipCache = false,
    recheckCongruence = false,
    resultsContainer = null,
    onProgress = null,
    onParsed = null,
  } = options;

  const finalResults = {};
  const resolutionResults = new Map();

  const getCachedResolution = (citation) => {
    const cachedResult = footnote.cached_results?.[String(citation?.id)];
    const resolution = cachedResult?.resolve_result || cachedResult ||
      (citation?.id === citations?.[0]?.id ? footnote.cached_resolution : null);
    if (!resolution || typeof resolution !== 'object') return null;
    if (resolution.status === 'error' || resolution.failure_reason) return null;
    if (resolution.file_id === undefined && resolution.method === undefined) return null;
    return resolution;
  };

  // STEP 1: PARSE
  if (onProgress) onProgress('parse', 'Parsing citations…');
  
  let claim, citations;
  try {
    const parseResult = await parseFootnote(footnote, {
      apiBase,
      getAuthHeaders,
      isWebApp,
      commonPayload,
      skipCache,
      resultsContainer,
    });
    claim = parseResult.claim;
    citations = parseResult.citations;
    // Keep the footnote item in sync so claim highlighting works after parse
    if (claim && claim.trim() && !footnote.is_inline) footnote.claim = claim;
    if (Array.isArray(citations)) footnote.parsed_citations = citations;
    onParsed?.({ claim, citations });
  } catch (error) {
    console.error('[Workflow] Parse failed:', error);
    throw error;
  }

  if (!citations || citations.length === 0) {
    if (onProgress) onProgress('complete', 'Parsing complete — no citations found');
    return finalResults;
  }

  // STEP 2: RESOLVE — group citations by the work they cite and resolve each
  // unique work ONCE, mirroring Verify All (footnotes.js). A footnote citing the
  // same work several times (e.g. several quotes from one case parsed as separate
  // citations) otherwise fires duplicate concurrent web resolutions that also race
  // each other's cache write, so the cache cannot dedup them either.
  if (onProgress) onProgress('resolve', 'Resolving sources…');

  const groups = new Map(); // resolution key -> [citation, ...]
  for (const citation of citations) {
    const k = resolutionKeyFromCitation(citation);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(citation);
  }

  const resolveGroup = async (key) => {
    const bucket = groups.get(key) || [];
    // Resolve the group's RICHEST member: short forms share a key with the full
    // citation, and the fullest one gives resolution the most signal.
    const sample = pickRichestCitation(bucket) || bucket[0];
    if (!sample) return;
    try {
      // Reuse any successful per-citation resolution. A failed resolution is
      // intentionally retried, while Check Again only refreshes congruence.
      const cachedRes = !skipCache
        ? getCachedResolution(bucket.find(citation => getCachedResolution(citation)))
        : null;

      const resolveResult = await resolveCitation(
        sample,
        footnote,
        citations,
        {
          apiBase,
          getAuthHeaders,
          isWebApp,
          commonPayload,
          cachedResolution: cachedRes,
          skipCache,
          // A Check Again reuses a successful resolution, but a failed cached
          // result must trigger a genuinely new hosted resolution attempt.
          forceResolutionRefresh: recheckCongruence && !cachedRes,
          resultsContainer,
          equivalentCitations: bucket,
        }
      );
      // Every member shares the group's result; the analyze endpoints re-key it
      // to each citation's own cite_id when persisting.
      for (const citation of bucket) {
        resolutionResults.set(citation.id, resolveResult);
        if (resultsContainer && citation.id !== sample.id) {
          updateCitationUI(resultsContainer, citation.id, { state: 'resolved', message: 'Resolved source' });
        }
      }
    } catch (error) {
      console.error(`[Workflow] Failed to resolve citation group ${key}:`, error);
      const errorMessage = extractErrorMessage(error);
      for (const citation of bucket) {
        resolutionResults.set(citation.id, { status: 'error', error: errorMessage });
        if (resultsContainer) {
          updateCitationUI(resultsContainer, citation.id, { state: 'error', message: 'Resolution failed' });
        }
        finalResults[citation.id] = {
          status: 'error',
          label: 'error',
          rationale: errorMessage,
          cite: citation?.source?.raw
        };
      }
    }
  };

  // Cross-references resolve against an already-resolved target, so run them
  // after the primaries — same two-phase ordering as Verify All.
  const groupKeys = Array.from(groups.keys());
  const isXrefKey = (k) => !!(groups.get(k) || [])[0]?.is_cross_reference;
  await Promise.all(groupKeys.filter(k => !isXrefKey(k)).map(resolveGroup));
  await Promise.all(groupKeys.filter(isXrefKey).map(resolveGroup));

  // STEP 3: ANALYZE (parallel)
  if (onProgress) onProgress('analyze', 'Checking congruence…');
  
  // Process all citations in parallel
  const analyzePromises = citations.map(async (citation) => {
    // Skip if already marked as error
    if (finalResults[citation.id]?.status === 'error') {
      return { citation, result: null };
    }

    try {
      const resolveResult = resolutionResults.get(citation.id);
      
      if (!resolveResult) {
        throw new Error('Missing resolution result for citation');
      }

      if (resolveResult.status === 'error') {
        return {
          citation,
          result: {
            status: 'error',
            label: 'error',
            rationale: resolveResult.error,
            cite: citation?.source?.raw
          }
        };
      }

      // Check for cached congruence (use first citation's cached congruence for single footnote)
      const cachedCong = (!citation.requires_joint_evaluation && !skipCache && !recheckCongruence && footnote.cached_congruence && citation.id === citations[0].id)
        ? footnote.cached_congruence
        : null;

      const analyzeResult = await analyzeCitation(
        citation,
        resolveResult,
        citation?.claim?.trim() ? citation.claim : claim,
        footnote.quote,
        {
          apiBase,
          getAuthHeaders,
          isWebApp,
          commonPayload,
          footnote,
          parsedCitations: citations,
          cachedCongruence: cachedCong,
          skipCache: skipCache || recheckCongruence,
          resultsContainer,
        }
      );
      
      return { citation, result: analyzeResult };
    } catch (error) {
      console.error(`[Workflow] Failed to analyze citation ${citation.id}:`, error);
      const errorMessage = extractErrorMessage(error);
      if (resultsContainer) {
        updateCitationUI(resultsContainer, citation.id, { state: 'error', message: 'Congruence failed' });
      }
      return {
        citation,
        result: {
          status: 'error',
          label: 'error',
          rationale: errorMessage,
          cite: citation?.source?.raw
        }
      };
    }
  });

  // Wait for all analyses to complete
  const analyzeResultsArray = await Promise.all(analyzePromises);
  
  // Store results in finalResults
  for (const { citation, result } of analyzeResultsArray) {
    if (result) {
      finalResults[citation.id] = result;
    }
  }

  const jointResults = {};
  const jointGroups = new Map();
  for (const citation of citations) {
    if (!citation.requires_joint_evaluation || !citation.joint_evaluation_group) continue;
    const groupId = citation.joint_evaluation_group;
    if (!jointGroups.has(groupId)) jointGroups.set(groupId, []);
    jointGroups.get(groupId).push(citation);
  }
  for (const [groupId, members] of jointGroups) {
    const memberResults = members.map(citation => finalResults[citation.id]?.joint_member_result || {
      cite_id: citation.id,
      contribution_label: 'non-verifiable',
      calibration: 0,
      rationale: finalResults[citation.id]?.rationale || 'Member check did not complete.',
      evidence: [], supported_claim_indices: [], quote_matches: [], unmatched_quotes: [],
    });
    try {
      const groupResult = await analyzeJointGroup(
        groupId, members.map(c => ({ ...c, resolve_result: resolutionResults.get(c.id) })),
        memberResults, members[0]?.claim?.trim() || claim, footnote,
        { apiBase, getAuthHeaders, isWebApp, commonPayload },
      );
      jointResults[groupId] = groupResult;
      for (const citation of members) {
        if (finalResults[citation.id]) finalResults[citation.id].joint_evaluation_group = groupId;
      }
    } catch (error) {
      jointResults[groupId] = { group_id: groupId, label: 'error', rationale: extractErrorMessage(error),
        member_cite_ids: members.map(c => c.id) };
    }
  }

  if (onProgress) onProgress('complete', 'Congruence check complete');
  
  // Return both results and parsed citations for type registration
  return { results: finalResults, parsedCitations: citations, jointResults };
}
